export type StageTone = 'light' | 'dark';

export type Rgb = { r: number; g: number; b: number };

export function parseCssColor(input: string | null | undefined): Rgb | null {
  if (!input) return null;
  const value = input.trim();
  const hex = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1];
    if (h.length === 3) {
      return {
        r: parseInt(h[0] + h[0], 16),
        g: parseInt(h[1] + h[1], 16),
        b: parseInt(h[2] + h[2], 16),
      };
    }
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }
  const rgb = value.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (rgb) {
    return {
      r: Number(rgb[1]),
      g: Number(rgb[2]),
      b: Number(rgb[3]),
    };
  }
  return null;
}

function linearize(channel: number): number {
  const s = channel / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(rgb: Rgb): number {
  return (
    0.2126 * linearize(rgb.r) +
    0.7152 * linearize(rgb.g) +
    0.0722 * linearize(rgb.b)
  );
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const l1 = relativeLuminance(a);
  const l2 = relativeLuminance(b);
  const light = Math.max(l1, l2);
  const dark = Math.min(l1, l2);
  return (light + 0.05) / (dark + 0.05);
}

export function mixSrgb(a: Rgb, b: Rgb, amountA: number): Rgb {
  const t = Math.min(1, Math.max(0, amountA));
  return {
    r: a.r * t + b.r * (1 - t),
    g: a.g * t + b.g * (1 - t),
    b: a.b * t + b.b * (1 - t),
  };
}

/**
 * Light tone when the Stage surface is light (dark ink).
 * When a background image covers the fill color, trust the foreground:
 * dark ink implies a light surface; light ink implies a dark surface.
 */
export function stageToneFromColors(
  foreground?: string | null,
  background?: string | null,
  backgroundImage?: string | null,
): StageTone {
  if (backgroundImage) {
    const fg = parseCssColor(foreground);
    if (fg && relativeLuminance(fg) < 0.4) return 'light';
    return 'dark';
  }
  const bg = parseCssColor(background);
  if (bg && relativeLuminance(bg) >= 0.4) return 'light';
  const fg = parseCssColor(foreground);
  if (!bg && fg && relativeLuminance(fg) < 0.4) return 'light';
  return 'dark';
}
